import { reactive, computed } from 'vue';
import { getAuth0Client } from './auth_wrapper';
import * as Sentry from "@sentry/vue";
import { safeSentryCapture } from '@/helpers/sentryUtils';

const auth = reactive({
  loading: true,
  isAuthenticated: false,
  user: null,
  auth0Client: null,
  error: null,
  token: null,
  initPromise: null,
  // Helper method to wait for auth0Client to be ready
  async waitForAuth() {
    if (this.initPromise) {
      await this.initPromise;
    }
    
    if (!this.auth0Client) {
      console.error('Auth0 client not ready - race condition detected');
      throw new Error('Auth0 client not initialized');
    }
    
    return this.auth0Client;
  },
  async loginWithRedirect(options) {
    console.log('Auth0 loginWithRedirect called');
    try {
      await this.waitForAuth();
      return this.auth0Client.loginWithRedirect(options);
    } catch (error) {
      console.error('Login error:', error);
      safeSentryCapture(Sentry.captureException, error, {
        tags: {
          error_type: 'auth0_login_redirect_error',
          error_source: 'auth_wrapper'
        },
        extra: {
          options: options,
          error_message: error.message,
          stack: error.stack,
          user_agent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        },
        level: 'error'
      });
      throw error;
    }
  },
  logout(options) {
    if (!this.auth0Client) {
      console.warn('Auth0 client not ready for logout');
      return Promise.resolve();
    }
    return this.auth0Client.logout(options);
  },
  async handleRedirectCallback() {
    try {
      await this.waitForAuth();
      const result = await this.auth0Client.handleRedirectCallback();
      this.isAuthenticated = await this.auth0Client.isAuthenticated();
      this.user = await this.auth0Client.getUser();
      this.token = await this.auth0Client.getTokenSilently();
      return result;
    } catch (e) {
      this.error = e;
      
      // Log auth callback errors to Sentry
      safeSentryCapture(Sentry.captureException, e, {
        tags: {
          error_type: 'auth0_handle_redirect_callback_error',
          error_source: 'auth_wrapper'
        },
        extra: {
          error_message: e.message,
          stack: e.stack,
          user_agent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        },
        level: 'error'
      });
    } finally {
      this.loading = false;
    }
  },
  async checkUser() {
    try {
      // Don't use waitForAuth here as it would create circular dependency during init
      if (!this.auth0Client) {
        throw new Error('Auth0 client not initialized');
      }
      this.isAuthenticated = await this.auth0Client.isAuthenticated();
      if (this.isAuthenticated) {
        this.user = await this.auth0Client.getUser();
        this.token = await this.auth0Client.getTokenSilently();
      }
    } catch (e) {
      this.error = e;
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
      
      // Log check user errors to Sentry
      safeSentryCapture(Sentry.captureException, e, {
        tags: {
          error_type: 'auth0_check_user_error',
          error_source: 'auth_wrapper'
        },
        extra: {
          error_message: e.message,
          stack: e.stack,
          user_agent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        },
        level: 'warning'
      });
    } finally {
      this.loading = false;
    }
  },
  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }
    
    console.log('Auth0 initialization starting');
    this.initPromise = (async () => {
      try {
        this.auth0Client = await getAuth0Client();
        await this.checkUser();
        console.log('Auth0 initialization completed');
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Log auth initialization errors to Sentry
        safeSentryCapture(Sentry.captureException, error, {
          tags: {
            error_type: 'auth0_init_error',
            error_source: 'auth_wrapper'
          },
          extra: {
            error_message: error.message,
            stack: error.stack,
            user_agent: navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString()
          },
          level: 'error'
        });
        this.error = error;
        this.loading = false;
        throw error;
      }
    })();
    
    return this.initPromise;
  }
});

// Add computed properties for convenience
auth.isReady = computed(() => !auth.loading && !!auth.auth0Client && !auth.error);
auth.hasError = computed(() => !!auth.error);

auth.init();

export default {
  install(app) {
    app.provide('auth', auth);
  },
};

export { auth };